import classNames from "classnames";
import { PantheonAvatarWithoutMenu } from "../../../components/avatar/Avatar";
import { GoogleGroupIcon } from "../../../components/google-group/GoogleGroupIcon";
import {
  fakeCompanyNameFromCompanyName,
  usePrivacyMode,
} from "../../../context-providers/Privacy";
import { AppetiteIcon } from "../appetite/AppetiteIcon";
import { SafeReportMetadataResponse } from "../Conversion";
import { formatAbbreviatedMoney } from "../Money";
import styles from "./DashboardReportCard.module.css";
import { colorForStatus } from "./StatusIcon";
import { readableStatus } from "./util";

type DashboardReportCardProps = {
  report: SafeReportMetadataResponse;
  assigneeLabel: string | undefined;
  assigneeImageUrl: string | null | undefined;
  declined: boolean;
  onClick: () => void;
};

const formatCreatedDate = (createdAtTs: string): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(createdAtTs + "Z"));

export const DashboardReportCard = ({
  report,
  assigneeLabel,
  assigneeImageUrl,
  declined,
  onClick,
}: DashboardReportCardProps) => {
  const { privacyModeEnabled } = usePrivacyMode();
  const companyName = report.report_metadata.company_info.company_name;
  const displayCompanyName = privacyModeEnabled
    ? fakeCompanyNameFromCompanyName(companyName)
    : companyName;
  const effectiveDate = report.report_metadata.company_info.effective_date;
  const isAsap = effectiveDate === "ASAP";
  const totalTiv = report.report_metadata.precomputed_metadata.total_tiv;
  const statusColor = colorForStatus(report.status);
  const shortStatus = readableStatus(report.status).split(" - ")[0];

  return (
    <div
      className={classNames(styles.card, { [styles.declined]: declined })}
      style={{ borderLeftColor: statusColor }}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <div className={styles.headerRow}>
        <span className={styles.companyName}>{displayCompanyName}</span>
        <span className={styles.tiv}>{formatAbbreviatedMoney(totalTiv)}</span>
      </div>
      <div className={styles.metaRow}>
        <PantheonAvatarWithoutMenu
          label={assigneeLabel}
          imageUrl={assigneeImageUrl}
          showName={true}
        />
        <GoogleGroupIcon groupName={report.google_group_email} />
        <span className={styles.metaSpacer} />
        <div className={styles.appetiteWrap}>
          <AppetiteIcon
            naicsInfo={report.report_metadata.naics_info}
            precomputedMetadata={report.report_metadata.precomputed_metadata}
            status={report.status}
            justifyContent="center"
            fontSize="10px"
          />
        </div>
      </div>
      <div className={styles.footerRow}>
        <span className={styles.status} style={{ color: statusColor }}>
          {shortStatus}
        </span>
        <span className={styles.sep}>·</span>
        <span>{effectiveDate}</span>
        {isAsap && (
          <i className="pi pi-clock" style={{ color: "var(--red-500)" }} />
        )}
        <span className={styles.sep}>·</span>
        <span className={styles.created}>
          {formatCreatedDate(report.created_at_ts)}
        </span>
      </div>
    </div>
  );
};
